import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckType from '../CheckType';
import TermsCheck from '../TermsCheck';
import CheckMessageButton from '../CheckMessageButton';
import { ToastContainer, toast, Slide } from 'react-toastify';
import { getOcrWorker, preprocessImage, normalizeUrls } from '../../utils/ocr';
import { useAppContext } from '../../context/AppContext';
import AnalyzeLoading from '../AnalyzeLoading';
import { useCmsText } from '../../hooks/useCmsText';
import { useAnalyzeMessageMutation } from '../../store/api';
import 'react-toastify/dist/ReactToastify.css';

function ArrowLeft({ color }: { color: string | undefined }) {
	return (
		<svg
			width="20"
			height="20"
			viewBox="0 0 25 25"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			transform="rotate(0 0 0)"
			stroke={color}
			aria-hidden="true"
		>
			<path
				d="M3.57813 12.4981C3.5777 12.6905 3.65086 12.8831 3.79761 13.0299L9.7936 19.0301C10.0864 19.3231 10.5613 19.3233 10.8543 19.0305C11.1473 18.7377 11.1474 18.2629 10.8546 17.9699L6.13418 13.2461L20.3295 13.2461C20.7437 13.2461 21.0795 12.9103 21.0795 12.4961C21.0795 12.0819 20.7437 11.7461 20.3295 11.7461L6.14168 11.7461L10.8546 7.03016C11.1474 6.73718 11.1473 6.2623 10.8543 5.9695C10.5613 5.6767 10.0864 5.67685 9.79362 5.96984L3.84392 11.9233C3.68134 12.0609 3.57812 12.2664 3.57812 12.4961L3.57813 12.4981Z"
				fill="#343C54"
			/>
		</svg>
	);
}

interface CheckerProps {
	type: 'text' | 'image';
}

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const Checker = ({ type }: CheckerProps) => {
	const { state, dispatch } = useAppContext();
	const navigate = useNavigate();
	const { renderText: renderWithLink, renderPlainText: renderText } =
		useCmsText();

	const [copiedText, setCopiedText] = useState<string>('');
	const [uploadedPhoto, setUploadedPhoto] = useState<string | null>(null);
	const [isDragOver, setIsDragOver] = useState<boolean>(false);

	const [analyzeMessage, { isLoading }] = useAnalyzeMessageMutation();

	const dropzoneRef = useRef<HTMLDivElement | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const cameraInputRef = useRef<HTMLInputElement | null>(null);

	const isMobile = useMemo(() => {
		if (typeof navigator === 'undefined') return false;
		return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
			navigator.userAgent
		);
	}, []);

	const notify = (msg: string) =>
		toast(msg, {
			toastId: 'file-warning',
			position: 'top-center',
			autoClose: 3000,
			hideProgressBar: true,
			closeOnClick: true,
			pauseOnHover: false,
			draggable: true,
			progress: undefined,
			transition: Slide,
			className: 'toast-error',
			style: {
				color: '#b30000',
				fontWeight: 'bold',
				background: 'transparent',
				textAlign: 'center',
				boxShadow: 'none',
			},
		});

	const handleBackToStart = () => {
		dispatch({ type: 'SET_COPIED_TEXT', payload: '' });
		dispatch({ type: 'SET_TERM_CHECK', payload: false });
		dispatch({ type: 'SET_SOURCE_TYPE', payload: '' });
		setCopiedText('');
		setUploadedPhoto(null);
		if (state.partnerName) {
			navigate(`/?partner=${state.partnerName}`);
		} else {
			navigate('/');
		}
	};

	const validateFile = (file: File) => {
		if (!ACCEPTED_TYPES.includes(file.type)) {
			notify('Unsupported file type. Please use PNG, JPG, or WEBP.');
			return false;
		}
		const sizeMB = file.size / (1024 * 1024);
		if (sizeMB > MAX_FILE_SIZE_MB) {
			notify(`File is too large. Max ${MAX_FILE_SIZE_MB} MB.`);
			return false;
		}
		return true;
	};

	const previewFile = (file: File) => {
		const reader = new FileReader();
		reader.onload = (e) => {
			if (e.target) {
				setUploadedPhoto(e.target.result as string);
			}
		};
		reader.readAsDataURL(file);
	};

	const handleFile = (file: File) => {
		toast.dismiss();
		if (!validateFile(file)) return;
		previewFile(file);
	};

	const handleUploadPhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		handleFile(file);
		event.currentTarget.value = '';
	};

	const handleRemovePhoto = () => {
		setUploadedPhoto(null);
		setCopiedText('');
	};

	const extractTextFromImage = async () => {
		if (!uploadedPhoto) return;
		try {
			const worker = await getOcrWorker();
			const prepped = await preprocessImage(uploadedPhoto);
			const { data } = await worker.recognize(prepped);
			const cleaned = normalizeUrls(data.text);
			setCopiedText(cleaned);
			dispatch({ type: 'SET_COPIED_TEXT', payload: cleaned });
		} catch (e) {
			console.error('OCR error:', e);
			notify('Could not read text from the image. Please try a clearer photo.');
		}
	};

	useEffect(() => {
		if (type === 'image' && uploadedPhoto) {
			extractTextFromImage().then(() => {
				if (state.termCheck) {
					handleCheck();
				}
			});
		}
	}, [uploadedPhoto]);

	const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
		if (!e.clipboardData) return;
		const items = e.clipboardData.items;
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (item.type.startsWith('image/')) {
				const blob = item.getAsFile();
				if (blob) {
					const file = new File([blob], 'pasted-image.png', {
						type: blob.type || 'image/png',
					});
					handleFile(file);
					e.preventDefault();
					return;
				}
			}
		}
	};

	const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(true);
	};
	const onDragLeave = () => setIsDragOver(false);
	const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDragOver(false);
		const file = e.dataTransfer.files?.[0];
		if (file) handleFile(file);
	};

	const handlePasteFromSystemClipboard = async () => {
		try {
			if (navigator.clipboard && navigator.clipboard.read) {
				const items: ClipboardItem[] = await navigator.clipboard.read();
				for (const item of items) {
					const type = item.types.find((t) => t.startsWith('image/'));
					if (type) {
						const blob = await item.getType(type);
						const file = new File([blob], 'pasted-image.png', { type });
						handleFile(file);
						return;
					}
				}
				notify('No image found in clipboard.');
			} else {
				notify(
					'Paste from clipboard is not supported by this browser. Try Ctrl/Cmd+V in the box.'
				);
			}
		} catch (err) {
			console.error(err);
			notify('Could not access clipboard.');
		}
	};

	const handlePasteTextClick = async () => {
		try {
			if (navigator.clipboard && navigator.clipboard.readText) {
				const clipboardText = await navigator.clipboard.readText();
				if (!clipboardText || clipboardText.trim() === '') {
					notify(
						'Clipboard is empty or contains unsupported data. Please copy text before pasting.'
					);
					return;
				}
				dispatch({ type: 'SET_COPIED_TEXT', payload: clipboardText });
			} else {
				notify(
					'Clipboard API not supported by this browser. Try manually pasting (Ctrl+V or Cmd+V).'
				);
			}
		} catch (error: any) {
			if (error.name === 'NotAllowedError') {
				notify(
					'Permission denied: please allow clipboard access in your browser settings.'
				);
			} else if (error.name === 'SecurityError') {
				notify(
					'Clipboard access blocked for security reasons. Try refreshing the page or pasting manually.'
				);
			} else {
				notify(
					'An unexpected error occurred while trying to read from clipboard.'
				);
			}
			console.error('Clipboard read error:', error);
		}
	};

	const handleCheck = async () => {
		if (type === 'image' && !state.copiedText) {
			notify(
				'Extracting text from image. Please wait a moment, then try again.'
			);
			return;
		}

		if (!state.termCheck) {
			notify('Please accept the data processing terms to continue.');
			return;
		}

		try {
			const result = await analyzeMessage({
				msg: state.copiedText,
				source: state.selectedSourceType,
				partner: state.partnerName || '',
				number: '12345',
				ocr: true,
				language: state.selectedLanguage,
			}).unwrap();

			const content = result.Content;
			const caseId = result.CaseId.toString();

			// Map Classification to result type
			let resultType = '';
			if (result.Classification === 'Danger') {
				resultType = 'risk';
			} else if (result.Classification === 'Warning') {
				resultType = 'warn';
			} else if (result.Classification === 'Safe') {
				resultType = 'safe';
			}

			dispatch({
				type: 'SET_RESULT',
				payload: {
					type: resultType,
					text: content,
					caseId,
				},
			});
			dispatch({ type: 'SET_COPIED_TEXT', payload: '' });
			dispatch({ type: 'SET_TERM_CHECK', payload: false });
			dispatch({ type: 'SET_SOURCE_TYPE', payload: '' });
			setCopiedText('');
			setUploadedPhoto(null);
			navigate('/result');
		} catch (error) {
			console.error('Error:', error);
			notify('Error in API response. Please try again.');
		}
	};

	const pasteShortcutHint = useMemo(() => {
		const isMac =
			typeof navigator !== 'undefined' &&
			/Mac|iPhone|iPad|iPod/i.test(navigator.platform || '');
		return isMac ? 'Cmd+V' : 'Ctrl+V';
	}, []);

	if (isLoading) {
		return <AnalyzeLoading />;
	}

	return (
		<div
			className="container mx-auto px-4 py-5 sm:px-8 md:px-12 mt-5"
			style={{ maxWidth: '600px' }}
		>
			<ToastContainer position="top-center" />

			<div
				onClick={handleBackToStart}
				className="w-full flex flex-row items-center justify-start cursor-pointer px-4 mb-5"
				role="button"
				aria-label={renderText('navigation_back', 'Tilbake') as string}
				tabIndex={0}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') handleBackToStart();
				}}
			>
				<ArrowLeft color={state.branding?.fontColor} />
				<p
					className="text-lg font-medium ml-2"
					style={{ color: state.branding?.fontColor }}
				>
					{renderText('navigation_back', 'Tilbake')}
				</p>
			</div>

			<div className="pl-4 sm:pl-8">
				<h1
					className="text-2xl sm:text-3xl font-medium"
					style={{ color: state.branding?.fontColor }}
				>
					{type === 'text'
						? renderText(
								'text_checker_header',
								'Lim inn teksten du ønsker å sjekke'
							)
						: renderText('image_checker_header', 'Last opp skjermbilde')}
				</h1>
			</div>

			<div className="w-full flex flex-col px-4 sm:px-6">
				{type === 'text' ? (
					<>
						<div className="w-full p-1 py-6 sm:py-10">
							<div className="text-center flex flex-row items-center justify-between">
								<h1
									className="text-lg sm:text-xl font-semibold"
									style={{ color: state.branding?.fontColor }}
								>
									{renderText('text_checker_label', 'Mistenkelig tekst')}
								</h1>
								<button
									onClick={handlePasteTextClick}
									className="flex flex-row items-center font-bold px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl border border-stone-300 bg-primary text-white focus:outline-none focus:ring-2 focus:ring-offset-2"
									style={{ background: state.branding?.mainButtonBackground }}
								>
									<img
										width={18}
										height={18}
										src="/images/white-clipboard.png"
										alt="paste"
										className="mr-2"
									/>
									{renderText('text_checker_button_paste', 'Lim inn')}
								</button>
							</div>
						</div>
						<textarea
							className="w-full h-32 sm:h-48 p-2 border border-stone-300 rounded-2xl placeholder:text-md"
							onChange={(e) =>
								dispatch({ type: 'SET_COPIED_TEXT', payload: e.target.value })
							}
							value={state.copiedText}
							aria-label="Suspicious text"
						/>
					</>
				) : (
					<>
						<div className="w-full py-6 sm:py-10">
							<div className="flex flex-col">
								<h3
									className="text-lg sm:text-lg font-semibold"
									style={{ color: state.branding?.fontColor }}
								>
									{renderText('image_checker_label', 'Skjermbilde')}
								</h3>
								<p
									className="text-sm sm:text-base mt-2"
									style={{ color: state.branding?.fontColor }}
								>
									{!isMobile
										? renderText('drag_drop_text', `Drag & drop, paste (${pasteShortcutHint}), or click to upload`)
										: renderText('drag_drop_text', 'Ta bilde eller last opp fra galleriet for øyeblikkelig sjekk')}
								</p>
							</div>
						</div>

						<div
							ref={dropzoneRef}
							onPaste={onPaste}
							onDragOver={onDragOver}
							onDragLeave={onDragLeave}
							onDrop={onDrop}
							tabIndex={0}
							aria-label={`Upload zone. ${!isMobile ? `Press ${pasteShortcutHint} to paste from clipboard.` : 'Use the Take Photo button to open camera.'}`}
							className={[
								'w-full flex flex-col items-center px-4 py-12 sm:px-6 border-dashed border-2 rounded-md p-3 transition outline-none',
								isDragOver
									? 'border-primary ring-2 ring-primary'
									: 'border-gray-300',
							].join(' ')}
							style={{ cursor: 'paste' as any }}
						>
							{!uploadedPhoto && (
								<div className="flex flex-col items-center gap-3 text-center">
									<img
										width={20}
										height={20}
										src="/images/upload-icon.png"
										alt="upload"
									/>
									<p className="text-base font-medium">
										{!isMobile
											? renderText('drag_drop_text', `Drag & drop, paste (${pasteShortcutHint}), or click to upload`)
											: renderText('drag_drop_text', 'Trykk “Ta bilde” for å åpne kamera, eller last opp fra galleriet')}
									</p>

									<label
										htmlFor="upload-photo"
										className="relative w-2/3 cursor-pointer border border-gray-300 rounded-3xl p-3 flex items-center justify-center focus-within:ring-2"
									>
										<span className="text-base font-medium">
											{renderText(
												'image_checker_button_upload',
												'Last opp skjermbilde'
											)}
										</span>
										<input
											id="upload-photo"
											ref={fileInputRef}
											type="file"
											className="sr-only"
											accept={ACCEPTED_TYPES.join(',')}
											onChange={handleUploadPhoto}
										/>
									</label>

									{!isMobile && (
										<button
											type="button"
											onClick={handlePasteFromSystemClipboard}
											className="underline text-sm"
										>
											{renderText(
												'paste_text',
												'Lim inn bilde fra utklippstavlen'
											)}
										</button>
									)}

									{isMobile && (
										<label
											htmlFor="camera-capture"
											className="relative w-2/3 cursor-pointer border border-gray-300 rounded-3xl p-3 flex items-center justify-center focus-within:ring-2"
										>
											<span className="text-base font-medium">Ta bilde</span>
											<input
												id="camera-capture"
												ref={cameraInputRef}
												type="file"
												className="sr-only"
												accept="image/*"
												capture="environment"
												onChange={handleUploadPhoto}
											/>
										</label>
									)}
								</div>
							)}

							{uploadedPhoto && (
								<div className="relative p-2 flex items-center justify-center">
									<img
										src={uploadedPhoto}
										alt="uploaded"
										className="max-w-40 h-auto"
									/>
									<button
										className="absolute -mt-2 -mr-2 w-5 h-5 top-0 right-0 bg-primary text-white rounded-lg flex items-center justify-center pb-1"
										onClick={handleRemovePhoto}
										aria-label="Remove photo"
									>
										×
									</button>
								</div>
							)}
						</div>
					</>
				)}
			</div>

			<CheckType
				heading={
					renderText('origin_question', 'Hvor er teksten/meldingen fra?') as string
				}
				options={[
					renderText('origin_email', 'E-post'),
					renderText('origin_sms', 'SMS'),
					renderText('origin_other', 'Annet'),
					renderText('origin_webpage', 'Nettside'),
					renderText('origin_social_media', 'Sosiale Medier'),
				]}
				onSelectOption={(option) =>
					dispatch({ type: 'SET_SOURCE_TYPE', payload: option })
				}
			/>

			<div className="w-full flex flex-col px-4 sm:px-8">
				<TermsCheck
					onTermCheck={(checked) =>
						dispatch({ type: 'SET_TERM_CHECK', payload: checked })
					}
					termsText={
						renderWithLink(
							'origin_terms',
							'Ja, jeg godtar at Skjold.ai behandler data i samsvar med vår personvernerklæring. Data lagres og benyttes for å forhindre nettbedrageri.'
						) as string
					}
				/>
				<CheckMessageButton handleCheck={handleCheck} />
			</div>
		</div>
	);
};

export default Checker;
